import { useEffect, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { useHeaderContext } from "~/common/HeaderContext"
import { useDocumentTitle } from "~/common/useDocumentTitle"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import {
  castingCallsPath,
  dashboardPath,
  newCastingCallPath,
} from "~/common/paths"
import { BackToContext, useSetBackTo } from "~/common/useBackNavigation"
import { Button } from "~/shadcn/ui/button"
import { Card, CardContent } from "~/shadcn/ui/card"
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "~/shadcn/ui/table"
import { InfiniteLoadMore } from "~/ui/InfiniteLoadMore"
import { numberDisplay } from "~/util/numbers"
import { SearchBar } from "./components/casting-call-filters/SearchBar"
import { BudgetCell } from "./components/casting-call-table/BudgetCell"
import { CampaignNameCell } from "./components/casting-call-table/CampaignNameCell"
import { CastingCallRow } from "./components/casting-call-table/CastingCallRow"
import { CategoryCell } from "./components/casting-call-table/CategoryCell"
import { CollaborationTypeCell } from "./components/casting-call-table/CollaborationTypeCell"
import { DatePostedCell } from "./components/casting-call-table/DatePostedCell"
import { PlatformsCell } from "./components/casting-call-table/PlatformsCell"
import { PostedByCell } from "./components/casting-call-table/PostedByCell"
import { useCastingCalls } from "./data/useCastingCalls"

export const AllCastingCallsScreen = () => {
  useDocumentTitle("Casting Calls")
  const { setHeaderText, setSubheaderText } = useHeaderContext()
  const currentUser = useCurrentUser()
  const navigate = useNavigate()
  const {
    loading,
    data,
    castingCalls,
    pageInfo,
    onLoadNextPage,
    setSearchValues,
  } = useCastingCalls({})

  useEffect(() => {
    setHeaderText("Casting Calls")
  }, [setHeaderText])

  useSetBackTo(BackToContext.CastingCall, castingCallsPath({}))

  const castingCallCount = data?.castingCalls.totalCount
  useTrackScreenEvent(
    "view_casting_calls",
    useMemo(
      () =>
        castingCallCount === undefined
          ? null
          : { tab: "all", count: castingCallCount },
      [castingCallCount]
    )
  )

  useEffect(() => {
    if (navigate && !currentUser.canIndexAllCastingCalls.value) {
      navigate(dashboardPath({}), { replace: true })
    }
  }, [navigate, currentUser])

  useEffect(() => {
    setSubheaderText(
      loading
        ? "Loading..."
        : `${numberDisplay(data?.castingCalls.totalCount)} Casting Calls`
    )
    return () => {
      setSubheaderText("")
    }
  }, [setSubheaderText, loading, data?.castingCalls.totalCount])

  return (
    <div className="h-full pb-4 relative">
      <Card className="absolute left-0 right-0 top-0 bottom-4 overflow-auto">
        <CardContent className="flex-1 p-6 pt-4 flex flex-col">
          <div className="flex justify-between items-center gap-4">
            <SearchBar setSearchValues={setSearchValues} />
            <Button
              variant="default"
              color="primary"
              className="ml-6"
              disabled={!currentUser.canCreateCastingCall.value}
            >
              <Link to={newCastingCallPath({})}>+ New Casting Call</Link>
            </Button>
          </div>
          <Card className="absolute top-[70px] bottom-4 left-6 right-6 flex-grow overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead sticky>Name</TableHead>
                  <TableHead sticky>Budget</TableHead>
                  <TableHead sticky>Category</TableHead>
                  <TableHead sticky>Type of Collaboration</TableHead>
                  <TableHead sticky>Platforms</TableHead>
                  <TableHead sticky>Posted By</TableHead>
                  <TableHead sticky>Date Posted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {castingCalls.map((castingCall) => (
                  <CastingCallRow
                    castingCall={castingCall}
                    key={castingCall.id}
                  >
                    <CampaignNameCell />
                    <BudgetCell />
                    <CategoryCell />
                    <CollaborationTypeCell />
                    <PlatformsCell />
                    <PostedByCell />
                    <DatePostedCell />
                  </CastingCallRow>
                ))}
              </TableBody>
              <InfiniteLoadMore
                onEndReached={onLoadNextPage}
                canLoadMore={!loading && !!pageInfo?.hasNextPage}
                loading={loading && castingCalls.length > 0}
                className="p-4"
              />
              {loading && <div>Loading...</div>}
            </Table>
          </Card>
        </CardContent>
      </Card>
    </div>
  )
}
